import React from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray, reduxForm, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import TextField from '../../app-main/components/reduxForms/fields/TextField/TextField';
import SelectField from '../../app-main/components/reduxForms/fields/SelectField/SelectField';
import NumberField from '../../app-main/components/reduxForms/fields/NumberField/NumberField';
import RenderDows from '../../app-main/components/reduxForms/fields/DowField/DowField';
import RadioField from '../../app-main/components/reduxForms/fields/RadioField/RadioField';
import SelectTimeFieldHOC from '../../app-main/components/reduxForms/fields/SelectTimeField/SelectTimeFieldHOC';
import { VARIABLE_PRICING_FORM } from './constants';
import Button from '../../elements-buttons/Button';
import onSubmitVariablePrice from './submit/onSubmitVariablePrice';
import { isNumber, get } from 'lodash';
import validateVariablePricingForm from './validate/validateVariablePricingForm';
import onChangePricingForm from './change/onChangePricingForm';
import { WeekData } from '../../elements-date-time/data/week-data';
import Currency from '../../elements-prices/Currency';
import { FormattedMessage, injectIntl, intlShape, defineMessages } from 'react-intl';

const VariablePricingForm = ({
  handleSubmit = null,
  pricingType,
  customStart,
  dispatch,
  handleCancel,
  error,
  listing,
  intl
}) => {
  const SelectTimeField = SelectTimeFieldHOC(intl, SelectField);
  const translationMessages = defineMessages({
    ratePlaceholder: {
      id: 'hosting.variable_pricing_form.rabe_placeholder',
      defaultMessage: 'e.g. Weekend Rate, Evening Rate'
    },
    rateErrorMessage: {
      id: 'hosting.variable_pricing_form.rate_error_message',
      defaultMessage: 'Please enter a valid name'
    },
    optionErrorMessage: {
      id: 'hosting.variable_pricing_form.option_error_message',
      defaultMessage: 'Please select an option'
    },
    timeErrorMessage: {
      id: 'hosting.variable_pricing_form.time_error_message',
      defaultMessage: 'Please enter a valid time'
    },
    allDayLabel: {
      id: 'hosting.variable_pricing_form.all_day_label',
      defaultMessage: 'All day'
    },
    afterLabel: {
      id: 'hosting.variable_pricing_form.after_label',
      defaultMessage: 'After'
    },
    customLabel: {
      id: 'hosting.variable_pricing_form.custom_label',
      defaultMessage: 'Custom'
    }
  });

  return (
    <form className="fandb-panel panel-body border margin-md-top margin-lg-bottom" onSubmit={handleSubmit} >
      <h4><FormattedMessage id="hosting.variable_pricing_form.heading" defaultMessage="New rate"/></h4>
      <div className="flex margin-xl-bottom column-sm">
        <div className="margin-md-right flex-basis-50">
          <label htmlFor="VariablePricingNameInput"><FormattedMessage id="hosting.variable_pricing_form.title_label" defaultMessage="Title"/></label>
          <Field
            id="VariablePricingNameInput"
            className="VariablePricingNameInput full-length-input-sm"
            component={TextField}
            name="name"
            attributes={{ maxLength: 30 }}
            placeholder={intl.formatMessage(translationMessages.ratePlaceholder)}
            errorMessage={intl.formatMessage(translationMessages.rateErrorMessage)}
          />
        </div>
        <div className="margin-md-right flex-basis-50">
          <label htmlFor="VariablePricingRateInput"><FormattedMessage id="hosting.variable_pricing_form.hourly_rate_label" defaultMessage="Hourly Rate"/></label>
          <Field
            id="VariablePricingRateInput"
            className="VariablePricingRateInput full-length-input-sm"
            component={NumberField}
            preInputElements={
              <Currency
                className="input-group-addon"
                id="base-rate"
                currency={get(listing, 'prices.0.currency')}
              />
            }
            name="rate"
          />
        </div>
      </div>
      <div>
        <h5><FormattedMessage id="hosting.variable_pricing_form.applies_to_heading" defaultMessage="Applies to"/></h5>
        <div style={{ marginBottom: '24px' }}>
          <label htmlFor="dow"><FormattedMessage id="hosting.variable_pricing_form.weekdays_label" defaultMessage="Weekdays"/></label>
          <FieldArray
            component={RenderDows}
            name="dows"
            className="margin-lg-bottom"
            errorMessage={intl.formatMessage(translationMessages.optionErrorMessage)}
            fields={WeekData}
          />
        </div>
        <div>
          <label htmlFor="time"><FormattedMessage id="hosting.variable_pricing_form.time_label" defaultMessage="Time"/></label>
          <Field
            component={RadioField}
            name="pricingType"
            className="CalendarPricingRadio flex baseline margin-lg-bottom column-xs flex-wrap"
            errorMessage={intl.formatMessage(translationMessages.optionErrorMessage)}
            onChange={
              () => {
                onChangePricingForm('customEnd', null, dispatch);
                onChangePricingForm('customStart', null, dispatch);
                onChangePricingForm('afterStart', null, dispatch);
              }
            }
            onBlur={(e) => e.preventDefault()}
            options={[
              {
                id: 'all-day',
                value: 'all-day',
                label: intl.formatMessage(translationMessages.allDayLabel)
              },
              {
                id: 'after',
                value: 'after',
                label: intl.formatMessage(translationMessages.afterLabel),
                component: (
                  <div className="start-only-time-input">
                    <Field
                      component={SelectTimeField}
                      name="afterStart"
                      errorMessage={intl.formatMessage(translationMessages.timeErrorMessage)}
                      isStartTime
                      isDisabled={!('after' === pricingType)}
                    />
                  </div>
                )
              },
              {
                id: 'custom',
                value: 'custom',
                label: intl.formatMessage(translationMessages.customLabel),
                displaySecondInput: true,
                component: (
                  <div className="flex baseline custom-time column-sm column-xs">
                    <Field
                      component={SelectTimeField}
                      name="customStart"
                      errorMessage={intl.formatMessage(translationMessages.timeErrorMessage)}
                      isStartTime
                      isDisabled={!('custom' === pricingType)}
                      onChange={
                        () => {
                          onChangePricingForm('customEnd', null, dispatch);
                        }
                      }
                    />
                    <div className="margin-sm-left margin-sm-right"><FormattedMessage id="hosting.variable_pricing_form.to" defaultMessage="to"/></div>
                    <Field
                      component={SelectTimeField}
                      name="customEnd"
                      errorMessage={intl.formatMessage(translationMessages.timeErrorMessage)}
                      startTimeIndex={customStart}
                      isDisabled={ ('custom' === pricingType && !isNumber(customStart)) || !('custom' === pricingType)}
                      isEndTime
                    />
                  </div>
                )
              }
            ]}
          />
        </div>
      </div>
      <div className="clearfix" />
      { error && <div className="text-danger">{error}</div> }
      <hr />
      <div className="flex end">
        <div className="flex-basis-15 margin-md-right">
          <Button category="default" type="button" onClick={handleCancel}><FormattedMessage id="hosting.variable_pricing_form.button_text_cancel" defaultMessage="Cancel"/></Button>
        </div>
        <div className="flex-basis-15">
          <Button category="primary" />
        </div>
      </div>
    </form>
  );
};

VariablePricingForm.propTypes = {
  handleSubmit: PropTypes.func,
  pricingType: PropTypes.string,
  customStart: PropTypes.number,
  dispatch: PropTypes.func,
  handleCancel: PropTypes.func,
  error: PropTypes.string,
  listing: PropTypes.object,
  intl: intlShape
};

const config = {
  form: VARIABLE_PRICING_FORM,
  enableReinitialize: true,
  onSubmit: onSubmitVariablePrice,
  validate: validateVariablePricingForm
};

const selector = formValueSelector(VARIABLE_PRICING_FORM);
const mapStateToProps = (state) => {
  return {
    pricingType: selector(state, 'pricingType'),
    customStart: selector(state, 'customStart'),
    customEnd: selector(state, 'customEnd'),
    afterStart: selector(state, 'afterStart'),
    pricingName: selector(state, 'name'),
    pricingRate: selector(state, 'rate'),
    pricingDows: selector(state, 'dows'),
    listing: get(state, 'listing.data'),
    calendarPrice: get(state, 'calendarPrice.data', {}),
    accessToken: get(state, 'auth.accessToken')
  };
};

export default connect(mapStateToProps)(reduxForm(config)(injectIntl(VariablePricingForm)));
